// static/table_engine_src/util/date.js

const DEFAULT_MIN_YEAR = 1900;
const DEFAULT_MAX_YEAR = 2100;

function toInt(value) {
  const n = Number(value);
  return Number.isInteger(n) ? n : NaN;
}

function clampYearBound(value, fallback) {
  const n = toInt(value);
  return Number.isFinite(n) ? n : fallback;
}

function daysInMonthUtc(year, month) {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function toIsoIfValid(year, month, day, { minYear, maxYear }) {
  if (!(year >= minYear && year <= maxYear)) return "";
  if (!(month >= 1 && month <= 12)) return "";

  const maxDay = daysInMonthUtc(year, month);
  if (!(day >= 1 && day <= maxDay)) return "";

  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function isDateCol(col) {
  return String(col?.type || "").toLowerCase() === "date";
}

export function dateParseOptionsForCol(col, fallback = {}) {
  const src = col && typeof col === "object" ? col : {};

  const orderRaw = String(src.dateOrder ?? fallback.dateOrder ?? "").trim().toUpperCase();

  let dayFirst;
  if (orderRaw === "DMY") dayFirst = true;
  else if (orderRaw === "MDY") dayFirst = false;
  else if (typeof src.dateDayFirst === "boolean") dayFirst = src.dateDayFirst;
  else if (typeof fallback.dayFirst === "boolean") dayFirst = fallback.dayFirst;
  else dayFirst = true;

  const minYear = clampYearBound(
    src.dateMinYear ?? fallback.minYear,
    DEFAULT_MIN_YEAR,
  );
  const maxYearRaw = clampYearBound(
    src.dateMaxYear ?? fallback.maxYear,
    DEFAULT_MAX_YEAR,
  );
  const maxYear = Math.max(minYear, maxYearRaw);

  return { dayFirst, minYear, maxYear };
}

export function normalizeDateValue(value, options = {}) {
  const minYear = clampYearBound(options.minYear, DEFAULT_MIN_YEAR);
  const maxYearRaw = clampYearBound(options.maxYear, DEFAULT_MAX_YEAR);
  const maxYear = Math.max(minYear, maxYearRaw);
  const dayFirst = options.dayFirst !== false;

  if (value instanceof Date) {
    const t = value.getTime();
    if (!Number.isFinite(t)) return "";
    return toIsoIfValid(
      value.getUTCFullYear(),
      value.getUTCMonth() + 1,
      value.getUTCDate(),
      { minYear, maxYear },
    );
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const d = new Date(value);
    const t = d.getTime();
    if (!Number.isFinite(t)) return "";
    return toIsoIfValid(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), {
      minYear,
      maxYear,
    });
  }

  const s0 = String(value ?? "").trim();
  if (!s0) return "";

  const isoLike = s0.match(/^(\d{4}-\d{2}-\d{2})(?:$|[Tt\s].*)/);
  if (isoLike) {
    const directIso = isoLike[1];
    const y = toInt(directIso.slice(0, 4));
    const m = toInt(directIso.slice(5, 7));
    const d = toInt(directIso.slice(8, 10));
    return toIsoIfValid(y, m, d, { minYear, maxYear });
  }

  let m = s0.match(/^(\d{4})[\/\-.](\d{1,2})[\/\-.](\d{1,2})$/);
  if (m) {
    const y = toInt(m[1]);
    const mo = toInt(m[2]);
    const d = toInt(m[3]);
    return toIsoIfValid(y, mo, d, { minYear, maxYear });
  }

  m = s0.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m) {
    const y = toInt(m[1]);
    const mo = toInt(m[2]);
    const d = toInt(m[3]);
    return toIsoIfValid(y, mo, d, { minYear, maxYear });
  }

  m = s0.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (m) {
    const a = toInt(m[1]);
    const b = toInt(m[2]);
    const y = toInt(m[3]);

    if (!Number.isFinite(a) || !Number.isFinite(b) || !Number.isFinite(y)) return "";

    // Resolve ambiguous DD/MM vs MM/DD:
    // - If one side is >12, it must be the day.
    // - Otherwise, use configured default.
    if (a > 12 && b <= 12) return toIsoIfValid(y, b, a, { minYear, maxYear });
    if (b > 12 && a <= 12) return toIsoIfValid(y, a, b, { minYear, maxYear });

    if (dayFirst) return toIsoIfValid(y, b, a, { minYear, maxYear });
    return toIsoIfValid(y, a, b, { minYear, maxYear });
  }

  return "";
}

export function isValidIsoDate(value, options = {}) {
  const s = String(value ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  return normalizeDateValue(s, options) === s;
}
